// MealImageEdit.jsx
import { useRef, useState } from "react";
import FormRow from "../../interface/FormRow";
import styled from "styled-components";
import ReactCrop, {
  centerCrop,
  convertToPixelCrop,
  makeAspectCrop,
} from "react-image-crop";
import toast from "react-hot-toast";
import ButtonRound from "../../interface/ButtonRound";
import { MdOutlineAddAPhoto } from "react-icons/md";
import setCanvasPreview from "./setCanvasPreview";

const ParentContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 70%;
`;

const ImageContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  max-height: 60vh;
  overflow: auto;
  padding: 1rem;
`;

const StyledImage = styled.img`
  max-height: 60vh;
  max-width: 80%;
  border-radius: 1rem;
  object-fit: contain;
`;

const CropButton = styled.div`
  margin-top: 1rem;
  align-self: bottom;
  position: sticky;
  bottom: 1rem;
  z-index: 10;
`;

const ASPECT_RATIO = 1; // Square
const MIN_DIMENSION = 300;

const MealImageEdit = ({ onClose, onImageUpdate }) => {
  const ImgRef = useRef(null);
  const previewCanvasRef = useRef(null);
  const [imgSrc, setImgSrc] = useState("");
  const [crop, setCrop] = useState();

  const onSelectFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      const imageUrl = reader.result?.toString() || "";
      const image = new Image();
      image.src = imageUrl;

      image.onload = () => {
        const { naturalWidth, naturalHeight } = image;

        if (naturalWidth < MIN_DIMENSION || naturalHeight < MIN_DIMENSION) {
          toast.error("Image must be at least 300x300px.");
          return setImgSrc("");
        }

        const MAX_WIDTH = 800;
        const MAX_HEIGHT = 800;

        let targetWidth = naturalWidth;
        let targetHeight = naturalHeight;

        if (naturalWidth > MAX_WIDTH || naturalHeight > MAX_HEIGHT) {
          const scale = Math.min(
            MAX_WIDTH / naturalWidth,
            MAX_HEIGHT / naturalHeight
          );
          targetWidth = naturalWidth * scale;
          targetHeight = naturalHeight * scale;
        }

        const canvas = document.createElement("canvas");
        canvas.width = targetWidth;
        canvas.height = targetHeight;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(image, 0, 0, targetWidth, targetHeight);

        // Use resized data URL
        setImgSrc(canvas.toDataURL("image/jpeg", 0.9));
      };
    };
    reader.readAsDataURL(file);
  };

  const onImageLoad = (e) => {
    const { width, height } = e.currentTarget;

    const crop = makeAspectCrop(
      {
        unit: "%",
        width: (MIN_DIMENSION / width) * 100,
      },
      ASPECT_RATIO,
      width,
      height
    );
    setCrop(centerCrop(crop, width, height));
  };

  const onCropChange = (pixelCrop, percentCrop) => {
    setCrop(percentCrop);
  };

  return (
    <>
      <FormRow label="Meal Image">
        <input type="file" accept="image/*" onChange={onSelectFile} />
      </FormRow>

      {imgSrc && (
        <ParentContainer>
          <ImageContainer>
            <ReactCrop
              crop={crop}
              onChange={onCropChange}
              keepSelection
              aspect={ASPECT_RATIO}
              minWidth={MIN_DIMENSION}
            >
              <StyledImage
                ref={ImgRef}
                src={imgSrc}
                alt="Upload"
                onLoad={onImageLoad}
              />
            </ReactCrop>
          </ImageContainer>

          <CropButton>
            <ButtonRound
              onClick={() => {
                const canvas = previewCanvasRef.current;
                setCanvasPreview(
                  ImgRef.current,
                  canvas,
                  convertToPixelCrop(
                    crop,
                    ImgRef.current.width,
                    ImgRef.current.height
                  )
                );
                const dataUrl = canvas.toDataURL("image/jpeg", 0.9); // high quality
                onImageUpdate(dataUrl);
                onClose?.();
              }}
            >
              <MdOutlineAddAPhoto />
            </ButtonRound>
          </CropButton>

          {crop && (
            <canvas
              ref={previewCanvasRef}
              style={{
                display: "none",
                borderRadius: "1rem",
              }}
            />
          )}
        </ParentContainer>
      )}
    </>
  );
};

export default MealImageEdit;
